import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NovaServiceService, Conversation } from '../../../services/nova-service.service';
import { Observable, Subscription } from 'rxjs';
import { UtcDateTimePipe } from '../../../pipes/utc-date-time.pipe';

interface ConversationGroup {
  title: string;
  conversations: Conversation[];
}

@Component({
  selector: 'app-nova-history',
  standalone: true,
  imports: [CommonModule, UtcDateTimePipe],
  templateUrl: './nova-history.component.html',
  styleUrl: './nova-history.component.scss'
})
export class NovaHistoryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() conversations: Conversation[] = [];
  @Input() currentConversation: Conversation | null = null;
  @Output() conversationSelected = new EventEmitter<string>();
  @Output() conversationRenamed = new EventEmitter<{id: string, title: string}>();
  @Output() conversationDeleted = new EventEmitter<string>();
  @Output() savedFilesClicked = new EventEmitter<void>();

  // Observable subscriptions
  conversations$: Observable<Conversation[]>;
  currentConversation$: Observable<Conversation | null>;
  private conversationsSubscription?: Subscription;

  editingConversationId: string | null = null;
  showContextMenu: string | null = null;
  conversationGroups: ConversationGroup[] = [];
  private isSavingOnEnter: boolean = false;

  // Search functionality
  isSearchOpen: boolean = false;
  searchQuery: string = '';
  filteredConversations: Conversation[] = [];

  // Saved Files view state (tracked in parent component)
  @Input() showSavedFiles: boolean = false;

  // Hover state for Saved Files section
  isHoveringSavedFiles: boolean = false;

  constructor(private novaService: NovaServiceService) {
    // Subscribe to conversations from the service
    this.conversations$ = this.novaService.conversations$;
    this.currentConversation$ = this.novaService.currentConversation$;
  }

  ngOnInit() {
    document.addEventListener('click', () => {
      this.hideContextMenu();
    });

    // Refresh conversations from API when component initializes
    this.novaService.refreshConversations();

    // Subscribe to conversations from the service
    this.conversationsSubscription = this.conversations$.subscribe(conversations => {
      this.conversations = conversations;
      this.filteredConversations = [...conversations];
      this.groupConversations();
    });

    // Initialize with current conversations
    this.filteredConversations = [...this.conversations];
    this.groupConversations();
  }

  ngOnChanges() {
    // Update filtered conversations when input changes
    this.filteredConversations = [...this.conversations];
    this.applySearchFilter();
    this.groupConversations();
  }

  private groupConversations() {
    if (!this.filteredConversations || this.filteredConversations.length === 0) {
      // If there's a search query but no results, show empty groups
      if (this.searchQuery.trim()) {
        this.conversationGroups = [];
      } else {
        // Use the service method for grouping conversations when no search
        this.conversationGroups = this.novaService.getConversationGroups();
      }
      return;
    }

    // Group filtered conversations based on their date
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    const todayConversations: Conversation[] = [];
    const yesterdayConversations: Conversation[] = [];
    const last7DaysConversations: Conversation[] = [];
    const thisMonthConversations: Conversation[] = [];
    const lastMonthConversations: Conversation[] = [];
    const olderConversations: Conversation[] = [];

    // Sort filtered conversations by updatedAt (newest first)
    const sortedFiltered = [...this.filteredConversations].sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });

    sortedFiltered.forEach(conversation => {
      const convDate = conversation.updatedAt ? new Date(conversation.updatedAt) : new Date(0);
      const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

      if (convDateOnly.getTime() === today.getTime()) {
        todayConversations.push(conversation);
      } else if (convDateOnly.getTime() === yesterday.getTime()) {
        yesterdayConversations.push(conversation);
      } else if (convDate >= last7Days) {
        last7DaysConversations.push(conversation);
      } else if (convDate >= thisMonth) {
        thisMonthConversations.push(conversation);
      } else if (convDate >= lastMonth && convDate <= lastMonthEnd) {
        lastMonthConversations.push(conversation);
      } else {
        olderConversations.push(conversation);
      }
    });

    const groups: Array<{title: string, conversations: Conversation[]}> = [];

    if (todayConversations.length > 0) {
      groups.push({ title: 'Today', conversations: todayConversations });
    }
    if (yesterdayConversations.length > 0) {
      groups.push({ title: 'Yesterday', conversations: yesterdayConversations });
    }
    if (last7DaysConversations.length > 0) {
      groups.push({ title: 'Last 7 days', conversations: last7DaysConversations });
    }
    if (thisMonthConversations.length > 0) {
      groups.push({ title: 'This month', conversations: thisMonthConversations });
    }
    if (lastMonthConversations.length > 0) {
      groups.push({ title: 'Last month', conversations: lastMonthConversations });
    }
    if (olderConversations.length > 0) {
      groups.push({ title: 'Older', conversations: olderConversations });
    }

    this.conversationGroups = groups;
  }

  ngOnDestroy() {
    document.removeEventListener('click', () => {
      this.hideContextMenu();
    });

    // Unsubscribe from conversations
    if (this.conversationsSubscription) {
      this.conversationsSubscription.unsubscribe();
    }
  }

  selectConversation(conversationId: string, event?: Event) {
    // Don't select conversation if we're currently editing it
    if (this.editingConversationId === conversationId) {
      if (event) {
        event.stopPropagation();
      }
      return;
    }
    this.conversationSelected.emit(conversationId);
  }

  onTitleClick(event: Event) {
    const target = event.target as HTMLElement;
    // Only stop propagation if we're editing (contentEditable is true)
    // Otherwise, let the click propagate to trigger conversation selection
    if (target.contentEditable === 'true') {
      event.stopPropagation();
      // Focus the element to ensure it's editable
      target.focus();
    }
    // If not editing, let the event bubble up to trigger selectConversation
  }

  editConversation(conversationId: string, event: Event) {
    event.stopPropagation();
    this.editingConversationId = conversationId;
    this.hideContextMenu();

    // Focus the contentEditable element after a short delay
    setTimeout(() => {
      const element = document.querySelector(`[data-conversation-id="${conversationId}"] .conversation-title`) as HTMLElement;
      if (element) {
        element.focus();
        // Select all text
        const range = document.createRange();
        range.selectNodeContents(element);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        // Prevent blur from firing when clicking inside
        element.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        }, { once: true });
      }
    }, 0);
  }

  saveConversationTitle(conversationId: string, event: Event) {
    // Don't save if we're currently saving via Enter key
    if (this.isSavingOnEnter) {
      return;
    }

    // Only save if we're actually editing this conversation
    if (this.editingConversationId !== conversationId) {
      return;
    }

    const element = event.target as HTMLElement;
    const trimmedTitle = element.textContent?.trim() || '';

    // Find the conversation to get the original title
    const conversation = this.conversations.find(c => c.id === conversationId);
    if (!conversation) {
      this.editingConversationId = null;
      return;
    }

    // If title is empty, immediately restore the original conversation title in the DOM
    if (!trimmedTitle) {
      element.textContent = conversation.title;
      this.editingConversationId = null;
      return;
    }

    // Only save if title actually changed
    if (conversation.title === trimmedTitle) {
      this.editingConversationId = null;
      return;
    }

    this.novaService.saveConversationTitle(conversationId, trimmedTitle);
    this.conversationRenamed.emit({
      id: conversationId,
      title: trimmedTitle
    });

    this.editingConversationId = null;
  }

  saveConversationTitleOnEnter(conversationId: string, event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault(); // Prevent default behavior
    keyboardEvent.stopPropagation(); // Stop event propagation
    const element = event.target as HTMLElement;
    const trimmedTitle = element.textContent?.trim() || '';

    // Find the conversation to get the original title
    const conversation = this.conversations.find(c => c.id === conversationId);
    if (!conversation) {
      this.editingConversationId = null;
      return;
    }

    // If title is empty, immediately restore the original conversation title in the DOM
    if (!trimmedTitle) {
      element.textContent = conversation.title;
      this.editingConversationId = null;
      return;
    }

    // Only save if title actually changed
    if (conversation.title === trimmedTitle) {
      this.editingConversationId = null;
      return;
    }

    // Set flag to prevent blur handler from firing
    this.isSavingOnEnter = true;

    this.novaService.saveConversationTitle(conversationId, trimmedTitle);
    this.conversationRenamed.emit({
      id: conversationId,
      title: trimmedTitle
    });

    this.editingConversationId = null;

    // Blur immediately (blur event will be ignored due to flag)
    element.blur();

    // Reset flag after a short delay
    setTimeout(() => {
      this.isSavingOnEnter = false;
    }, 0);
  }

  cancelEditConversation() {
    if (this.editingConversationId) {
      // Find the conversation element and restore original title if empty
      const element = document.querySelector(`[data-conversation-id="${this.editingConversationId}"] .conversation-title`) as HTMLElement;
      if (element) {
        const trimmedTitle = element.textContent?.trim() || '';
        const conversation = this.conversations.find(c => c.id === this.editingConversationId);
        if (conversation && !trimmedTitle) {
          element.textContent = conversation.title;
        }
      }
    }
    this.editingConversationId = null;
  }

  confirmDeleteConversation(conversationId: string, event: Event) {
    event.stopPropagation();
    this.novaService.confirmDeleteConversation(conversationId);
    this.conversationDeleted.emit(conversationId);
    this.hideContextMenu();
  }

  toggleContextMenu(conversationId: string, event: Event) {
    event.stopPropagation();
    this.showContextMenu = this.showContextMenu === conversationId ? null : conversationId;
  }

  hideContextMenu() {
    this.showContextMenu = null;
  }

  trackByConversationId(index: number, conversation: Conversation): string {
    return conversation.id;
  }

  openNovaHistory(): void {
    this.novaService.openNovaHistory();
  }

  closeNovaHistory(): void {
    this.novaService.closeNovaHistory();
  }

  // Search functionality methods
  toggleSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.clearSearch();
    }
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.applySearchFilter();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearchOpen = false;
    this.filteredConversations = [...this.conversations];
    this.groupConversations();
  }

  private applySearchFilter(): void {
    if (!this.searchQuery.trim()) {
      this.filteredConversations = [...this.conversations];
    } else {
      this.filteredConversations = this.conversations.filter(conversation =>
        conversation.title.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
    this.groupConversations();
  }

  // Emit event when Saved Files is clicked
  onSavedFilesClick(): void {
    // Close search when switching views
    if (this.isSearchOpen) {
      this.clearSearch();
    }
    this.savedFilesClicked.emit();
  }
}
